import { Button, Image } from "@neos/design-system";
import { FetchApiClient, NavigationOptions, NavigationTarget, Reporting, navigate } from "@neos/core";
import { Fragment, computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, defineComponent, inject, normalizeClass, normalizeStyle, onScopeDispose, openBlock, provide, ref, resolveComponent, watch } from "vue";
import { getBaseUrl } from "@neos/shared";
//#region src/composables/useViewModel.ts
var viewModelKey = Symbol("ViewModel");
/**
* Provide the ViewModel to child components.
*
* @param viewModelName - ViewModel name.
* @param viewModel - ViewModel.
* @returns ViewModel.
*/
function provideViewModel(viewModel) {
	provide(viewModelKey, viewModel);
	return viewModel;
}
function useViewModel(throwError = true) {
	const viewModel = inject(viewModelKey, void 0);
	if (!viewModel && throwError) throw new Error("The view model is not provided.");
	return viewModel;
}
//#endregion
//#region \0plugin-vue:export-helper
var _plugin_vue_export_helper_default = (sfc, props) => {
	const target = sfc.__vccOpts || sfc;
	for (const [key, val] of props) target[key] = val;
	return target;
};
//#endregion
//#region src/composables/useApplicationContext.ts
function provideApplicationContext(applicationContext) {
	provide("applicationContext", applicationContext);
	return applicationContext;
}
function useApplicationContext(throwError = true) {
	const applicationContext = inject("applicationContext", void 0);
	if (!applicationContext && throwError) throw new Error("ApplicationContext not provided");
	return applicationContext;
}
//#endregion
//#region src/navigation/Router.ts
/** Represents informations about a route */
var RouteInfo = class {
	constructor() {
		this.creationMode = false;
		this.single = false;
		this.target = NavigationTarget.default;
	}
	/**
	* Converts to navigation options.
	*
	* @returns The navigation options, or undefined if the route info doesn't
	* contain enough information to be converted (e.g. no uiViewName)
	*/
	toNavigationOptions(uiViewName) {
		const realUIViewName = uiViewName ?? this.uiViewName;
		if (!realUIViewName) return;
		return (this.remote && !uiViewName ? NavigationOptions.withRemote(this.remote, realUIViewName) : new NavigationOptions(realUIViewName)).withId(this.id).withCreationMode(this.creationMode).withFrameId(this.frameId).withTitle(this.title).withMenuItemName(this.menuItemName).withUrlParameters(new Map(this.frameUrlParameters)).onTarget(this.target);
	}
};
/** Represents the route management */
var Router = class Router {
	static {
		this.frameParamPrefix = "f_";
	}
	constructor(eventManager) {
		this.routeQueryChangedHandler = this.onRouteQueryChanged.bind(this);
		this.eventManager = eventManager;
	}
	get enabled() {
		return !window.frameElement && window === window.top;
	}
	/**
	* Rebuild the URL Query string by adding a UIView in the route and change the current location.
	* @param viewModel - the view-model of the uiView
	* @param addToHistory - a value indicating whether the route should be added to the navigation history
	*/
	pushFrame(frame, addToHistory) {
		if (!this.enabled) return;
		const previousParams = new URLSearchParams(window.location.search);
		const hashSubFragments = window.location.hash.slice(1).split("?");
		const queryEmbeddedInHash = hashSubFragments.length >= 2 ? hashSubFragments[1] : "";
		const previousLegacyParams = new URLSearchParams(queryEmbeddedInHash);
		const params = new URLSearchParams();
		previousLegacyParams.forEach((val, key) => {
			previousParams.set(key, val);
		});
		previousParams.forEach((val, key) => {
			if (!key.startsWith(Router.frameParamPrefix)) params.set(key, val);
		});
		params.set("ui", frame.uiViewName);
		this.toggleParam(params, "r", frame.remote);
		this.toggleParam(params, "frameId", frame.id);
		this.toggleParam(params, "title", frame.explicitTitle ?? void 0);
		this.toggleParam(params, "mi", frame.menuItemName ?? void 0);
		this.toggleParam(params, "t", frame.navigationTarget !== NavigationTarget.default, () => NavigationTarget[frame.navigationTarget]);
		if (frame.viewModel) {
			this.toggleParam(params, "id", frame.viewModel.currentId, (id) => Router.idMapToString(id));
			this.toggleParam(params, "cm", frame.viewModel.creationMode, () => "y");
		}
		frame.frameUrlParameters.forEach((val, key) => {
			params.set(Router.frameParamPrefix + key, val);
		});
		this.pushQuery(params.toString(), addToHistory);
	}
	/**
	* Adds a query to the current route.
	* @param query - the query to add.
	* @param addToHistory - a value indicating whether the route should be added to the navigation history
	*/
	pushQuery(query, addToHistory) {
		if (!this.enabled) return;
		query = query ? `?${query}` : "";
		if (window.location.search === query) return;
		const url = query || location.pathname;
		if (addToHistory) history.pushState(void 0, "", url);
		else history.replaceState(void 0, "", url);
	}
	/**
	* Gets the informations about the current route for (re)initializing the Vue Application
	* (e.g. after redirection/navigation or if the page was refreshed).
	* @returns
	*/
	createRouteInfoForVueApp() {
		const query = window.location.hash.slice(1).split("?");
		const uiViewName = query[0];
		const search = uiViewName || window.location.protocol == "blob:" ? query[1] : window.location.search;
		return Router.createRouteInfo(search, uiViewName);
	}
	/**
	* Creates a route info based solely on a search query, without taking the uiViewName into account.
	* @param search - the search query extracted from a click on a hyperlink in the report viewer
	* @returns
	*/
	static createRouteInfoForReportViewer(search) {
		return Router.createRouteInfo(search);
	}
	/**
	* Creates a route info from a search query.
	* @param search - the search query
	* @param uiViewName - the uiView name (if any)
	* @returns
	*/
	static createRouteInfo(search, uiViewName) {
		const routeInfo = new RouteInfo();
		const params = new URLSearchParams(search);
		uiViewName ||= params.get("ui");
		routeInfo.uiViewName = uiViewName ?? void 0;
		params.delete("ui");
		routeInfo.remote = params.get("r") ?? void 0;
		params.delete("r");
		routeInfo.creationMode = params.get("cm") === "y";
		params.delete("cm");
		routeInfo.single = params.get("single") === "y";
		params.delete("single");
		const id = params.get("id");
		if (id) routeInfo.id = Router.toIdMap(id);
		params.delete("id");
		const frameId = params.get("frameId");
		if (frameId) routeInfo.frameId = frameId;
		params.delete("frameId");
		const title = params.get("title");
		if (title !== null) routeInfo.title = title;
		params.delete("title");
		const menuItemName = params.get("mi");
		if (menuItemName !== null) routeInfo.menuItemName = menuItemName;
		params.delete("mi");
		const target = params.get("t");
		if (target) routeInfo.target = NavigationTarget[target] ?? NavigationTarget.default;
		params.delete("t");
		const frameUrlParameters = new URLSearchParams();
		const extraUrlGlobalParameters = new URLSearchParams();
		params.forEach((val, key) => {
			if (key.startsWith(Router.frameParamPrefix)) frameUrlParameters.set(key.slice(2), val);
			else extraUrlGlobalParameters.set(key, val);
		});
		routeInfo.frameUrlParameters = frameUrlParameters;
		routeInfo.extraUrlGlobalParameters = extraUrlGlobalParameters;
		return routeInfo;
	}
	/**
	* Starts the router.
	*/
	start() {
		if (!this.enabled) return;
		window.addEventListener("popstate", this.routeQueryChangedHandler);
	}
	/** Stops the router */
	stop() {
		window.removeEventListener("popstate", this.routeQueryChangedHandler);
	}
	/**
	* Gets a value indicating whether the specified ids are identical.
	* @param id1
	* @param id2
	* @returns
	*/
	static isSameId(id1, id2) {
		return this.idMapToString(id1) === this.idMapToString(id2);
	}
	onRouteQueryChanged() {
		const navigationOptions = this.createRouteInfoForVueApp().toNavigationOptions();
		if (navigationOptions) navigate(this.eventManager, navigationOptions);
	}
	static toIdMap(value) {
		const kvs = value.split(",");
		const idMap = /* @__PURE__ */ new Map();
		for (const kv of kvs) {
			const item = kv.split(":");
			idMap.set(item[0], item[1]);
		}
		return idMap;
	}
	static idMapToString(value) {
		let str = "";
		for (const [key, val] of value) {
			if (str) str += ",";
			str += `${key}:${val}`;
		}
		return str;
	}
	toggleParam(params, key, value, getStringValue) {
		if (value) params.set(key, getStringValue ? getStringValue(value) : value);
		else params.delete(key);
	}
};
//#endregion
//#region src/composables/useUIViewPermitter.ts
function provideUIViewPermitter(uiViewPermitter) {
	provide("uiViewPermitter", uiViewPermitter);
	return uiViewPermitter;
}
function useUIViewPermitter() {
	const uiViewPermitter = inject("uiViewPermitter");
	if (!uiViewPermitter) throw new Error("UIViewPermitter not provided");
	return uiViewPermitter;
}
//#endregion
//#region src/composables/useEventManager.ts
function provideEventManager(eventManager) {
	provide("eventManager", eventManager);
	return eventManager;
}
function useEventManager(throwError = true) {
	const eventManager = inject("eventManager", void 0);
	if (!eventManager && throwError) throw new Error("EventManager not provided");
	return eventManager;
}
//#endregion
//#region src/composables/useDelegatedLoading.ts
/** Manages the delegate loading attribute for components preloading their content. */
function useDelegatedLoading(applicationContext, props) {
	const componentSource = ref();
	let disposed = false;
	function revokeObjectURL() {
		if (componentSource.value) URL.revokeObjectURL(componentSource.value);
	}
	watch([() => props.source, () => props.delegatedLoading], async ([source, delegatedLoading]) => {
		if (!delegatedLoading || !source) {
			revokeObjectURL();
			componentSource.value = source;
			return;
		}
		const res = await FetchApiClient.fetchWithTimeout(new Request(source, {
			method: "GET",
			headers: FetchApiClient.getHeaders(applicationContext),
			mode: "cors"
		}), FetchApiClient.getDefaultTimeout(applicationContext.webApiTimeout));
		if (disposed) return;
		const blob = await res.blob();
		if (disposed) return;
		revokeObjectURL();
		componentSource.value = getBlobUrl(source, blob);
	}, { immediate: true });
	function getBlobUrl(source, blob) {
		const url = new URL(source, getBaseUrl());
		const urlSearchParams = new URLSearchParams(url.search);
		let queryString = "";
		let uiName;
		urlSearchParams.forEach((v, k) => {
			if (k === "ui") uiName = v;
			else queryString += `${k}=${encodeURIComponent(v)}&`;
		});
		let blobUrl = URL.createObjectURL(blob);
		if (uiName) blobUrl += `#${encodeURIComponent(uiName)}`;
		if (queryString) blobUrl += `${uiName ? "" : "#"}?${queryString}`;
		return blobUrl;
	}
	onScopeDispose(() => {
		disposed = true;
		revokeObjectURL();
	});
	return { componentSource };
}
//#endregion
//#region src/components/WebView/WebView.ts?vue&type=script&src=true&lang.ts
var WebView_ts_vue_type_script_src_true_lang_default = defineComponent({
	name: "WebView",
	components: {
		Button,
		Image
	},
	props: {
		name: {
			type: String,
			default: void 0
		},
		title: {
			type: String,
			default: "webview"
		},
		width: {
			type: String,
			default: "100%"
		},
		height: {
			type: String,
			default: "100%"
		},
		source: {
			type: String,
			default: void 0
		},
		expandable: {
			type: Boolean,
			default: true
		},
		fullscreen: {
			type: Boolean,
			default: false
		},
		delegatedLoading: {
			type: Boolean,
			default: true
		}
	},
	emits: ["click"],
	setup(props, context) {
		const { componentSource } = useDelegatedLoading(useApplicationContext(), props);
		const style = computed(() => `height:${props.height};width:${props.width}`);
		const iFrameClassNames = ref();
		const modeFullScreenOn = ref(false);
		function setFullScreen() {
			iFrameClassNames.value = "neos-fullscreen-iframe-in-web-view";
			modeFullScreenOn.value = true;
		}
		function removeFullScreen() {
			iFrameClassNames.value = "";
			modeFullScreenOn.value = false;
		}
		if (props.fullscreen) setFullScreen();
		window.addEventListener("message", (e) => {
			if (e.origin == document.location.origin && typeof e.data === "string") context.emit("click", e.data);
		});
		return {
			style,
			iFrameClassNames,
			modeFullScreenOn,
			setFullScreen,
			removeFullScreen,
			iframeSource: componentSource
		};
	}
});
//#endregion
//#region src/components/WebView/WebView.vue
var _hoisted_1 = { class: "neos-web-view" };
var _hoisted_2 = [
	"name",
	"title",
	"width",
	"height",
	"src"
];
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
	const _component_Button = resolveComponent("Button");
	return openBlock(), createElementBlock("div", _hoisted_1, [createElementVNode("iframe", {
		name: _ctx.name,
		class: normalizeClass(["neos-iframe-in-web-view", _ctx.iFrameClassNames]),
		style: normalizeStyle(_ctx.style),
		title: _ctx.title,
		width: _ctx.width,
		height: _ctx.height,
		src: _ctx.iframeSource
	}, null, 14, _hoisted_2), _ctx.expandable ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [!_ctx.modeFullScreenOn ? (openBlock(), createBlock(_component_Button, {
		key: 0,
		class: "action-set-fullscreen",
		icon: "fullscreen",
		variant: "link",
		size: "small",
		onClick: _ctx.setFullScreen
	}, null, 8, ["onClick"])) : (openBlock(), createBlock(_component_Button, {
		key: 1,
		class: "action-unset-fullscreen",
		icon: "fullscreenExit",
		variant: "link",
		size: "small",
		onClick: _ctx.removeFullScreen
	}, null, 8, ["onClick"]))], 64)) : createCommentVNode("", true)]);
}
var WebView_default = /*#__PURE__*/ _plugin_vue_export_helper_default(WebView_ts_vue_type_script_src_true_lang_default, [["render", _sfc_render]]);
//#endregion
//#region src/components/ReportViewer/ReportViewer.vue
var ReportViewer_default = /* @__PURE__ */ defineComponent({
	__name: "ReportViewer",
	props: { options: {} },
	setup(__props) {
		const props = __props;
		const viewModel = useViewModel();
		const baseUrl = computed(() => {
			return (props.options.remote ?? viewModel.getRemote())?.reportingBaseUrl ?? Reporting.getBaseUrl();
		});
		const url = computed(() => Reporting.getReportViewerUrl(baseUrl.value, props.options, viewModel.uiViewName));
		const onClick = function(link) {
			if (typeof link === "string") {
				const search = link.split("?")[1];
				if (search) {
					const navigationOptions = Router.createRouteInfoForReportViewer(search).toNavigationOptions();
					if (navigationOptions) navigate(viewModel.eventManager, navigationOptions);
				}
			}
		};
		return (_ctx, _cache) => {
			return openBlock(), createBlock(WebView_default, {
				source: url.value,
				"delegated-loading": false,
				onClick
			}, null, 8, ["source"]);
		};
	}
});
//#endregion
//#region src/composables/useNotificationRegistryFactory.ts
function provideNotificationRegistryFactory(notificationRegistryFactory) {
	provide("notificationRegistryFactory", notificationRegistryFactory);
	return notificationRegistryFactory;
}
function useNotificationRegistryFactory() {
	const notificationRegistryFactory = inject("notificationRegistryFactory");
	if (!notificationRegistryFactory) throw new Error("NotificationRegistryFactory not provided");
	return notificationRegistryFactory;
}
//#endregion
//#region src/composables/useCustomViewManager.ts
function provideCustomViewManager(customViewManager) {
	provide("customViewManager", customViewManager);
	return customViewManager;
}
function useCustomViewManager() {
	const customViewManager = inject("customViewManager");
	if (!customViewManager) throw new Error("CustomViewManager not provided");
	return customViewManager;
}
//#endregion
export { useViewModel as _, ReportViewer_default as a, useEventManager as c, RouteInfo as d, Router as f, provideViewModel as g, _plugin_vue_export_helper_default as h, useNotificationRegistryFactory as i, provideUIViewPermitter as l, useApplicationContext as m, useCustomViewManager as n, WebView_default as o, provideApplicationContext as p, provideNotificationRegistryFactory as r, provideEventManager as s, provideCustomViewManager as t, useUIViewPermitter as u, viewModelKey as v };

//# sourceMappingURL=useCustomViewManager-DOMYH5P-.js.map